import React, { useState, useEffect } from 'react';
import { Link, navigate } from 'gatsby';
import { Typography, makeStyles } from '@material-ui/core';
import LockSharpIcon from '@material-ui/icons/LockSharp';
import { useTheme } from '@material-ui/core/styles';
import useWalletPayment from 'payment/hooks/use-wallet-payment';
import { useOAT } from 'common/providers/oatProvider';
import useConfig from 'common/providers/configProvider/use-config';
import { LabelButton } from 'common/components/Buttons/LabelButton';
import {
  Layout,
  Button,
  NewLoadingSpinner,
  NewNotificationBanner,
  TextButton,
} from '../../../common/components';
import PaymentButton from './PaymentButton';
import CreditCardIcon from '../../icons/CreditCard';
import { useContent } from '../../../content/content-provider';
import formatPrice from '../../../common/utils/format-price';
import GiftCardWrapper from './GiftCardWrapper';
import GiftCardSummary from './GiftCardSummary';
import { getItemWithExpiry, setItemWithExpiry } from '../../../common/utils/storage';
import { errorCodes } from '../../../common/utils/error-codes';

const styles = makeStyles(({ palette, spacing }: any) => ({
  paymentSecureContainer: {
    textAlign: 'center',
    flexGrow: 1,
    paddingTop: spacing(1.75),
    paddingBottom: spacing(1.25),
  },
  totalAmount: {
    display: 'flex',
    marginTop: spacing(6),
    justifyContent: 'space-between',
    backgroundColor: palette.secondary.lightMain,
    borderRadius: spacing(2),
    padding: spacing(4),
    alignItems: 'center',
  },
  paymentIcon: {
    paddingBottom: spacing(2.75),
    transform: 'scale(1.5)',
  },
  viewBill: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: spacing(6),
  },
  textContainer: {
    flexGrow: 1,
  },
  buttonsContainer: {
    flexShrink: 0,
    paddingBottom: spacing(1.25),
  },
}));

const mapBanners = (banners: string[]) =>
  banners ? banners.map((bannerCode) => errorCodes[bannerCode]) : [];

interface SelectPaymentWrapperProps {
  handleGoBack: () => void;
  closeGiftCardModal: () => void;
  useGiftCard: (string) => void;
  getGiftCardBalance: (string) => void;
  completePayment: () => void;
  removeGiftCard: (string) => void;
}

const SelectPaymentWrapper = ({
  handleGoBack,
  closeGiftCardModal,
  useGiftCard,
  getGiftCardBalance,
  completePayment,
  removeGiftCard,
}: SelectPaymentWrapperProps) => {
  const [content] = useContent();
  const classes = styles();
  const theme = useTheme();
  const {
    getPagePath,
    content: {
      common: { currency, autoHideDuration },
    },
    features: { giftCardsEnabled, paymentClientV2 },
    paymentMode,
  } = useConfig();

  const {
    data: {
      cardPaymentAmount,
      giftCardModal,
      giftCardQuery,
      totalToPay,
      giftCards = [],
      giftCardError,
      giftCardErrorTime,
      banners,
    },
  } = useOAT();

  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [errorBanner, setErrorBanner] = useState(() => mapBanners(banners));

  useWalletPayment();

  useEffect(() => {
    const bannerErrors = mapBanners(banners);

    if (bannerErrors.length > 0) {
      setItemWithExpiry(bannerErrors, 'current-errors', autoHideDuration);
    }
    const existingErrors = getItemWithExpiry('current-errors');

    if (existingErrors) {
      setErrorBanner(existingErrors);
    } else {
      setErrorBanner([]);
    }
    // eslint-disable-next-line no-autofix/react-hooks/exhaustive-deps
  }, [banners]);

  const handleCompletePayment = () => {
    setLoadingSpinner(true);
    completePayment();
  };

  const handleViewBill = () => {
    navigate(getPagePath('payment-bill'));
  };

  const cardPaymentPage = paymentMode ? getPagePath('pdq-payment') : getPagePath('pay-by-card');

  return (
    <Layout currentPage={getPagePath('select-payment')} paymentPage onClickBackArrow={handleGoBack}>
      <NewNotificationBanner
        open={!!errorBanner.length}
        onClose={() => setErrorBanner([])}
        variant="error"
        autoHideDuration={autoHideDuration}
        messages={errorBanner.map(({ text }) => content[text])}
      />
      <div className={classes.textContainer}>
        <Typography variant="h1">{content.title}</Typography>
        {giftCards.length ? (
          <GiftCardSummary
            totalToPay={totalToPay}
            cardPaymentAmount={cardPaymentAmount}
            currency={currency}
            giftCards={giftCards}
            removeGiftCard={removeGiftCard}
          />
        ) : (
          <div className={classes.totalAmount} data-testid="selectPaymentGrandTotal">
            <Typography variant="h3" component="h2">
              {content.total}
            </Typography>
            <Typography variant="h3" component="h2">
              {formatPrice(cardPaymentAmount, currency)}
            </Typography>
          </div>
        )}
      </div>
      <div className={classes.paymentSecureContainer}>
        {!giftCards.length && (
          <>
            <div className={classes.paymentIcon}>
              <LockSharpIcon aria-label="Padlock Icon" />
            </div>
            <Typography>{content.paymentSecure}</Typography>
          </>
        )}
      </div>
      <div className={classes.buttonsContainer}>
        {!!cardPaymentAmount && <PaymentButton />}
        {!!cardPaymentAmount && (
          <Link to={cardPaymentPage} state={{ paymentClientV2 }}>
            <LabelButton label={content.payByCard} icon={<CreditCardIcon />} />
          </Link>
        )}
        {!!cardPaymentAmount && giftCardsEnabled && (
          <GiftCardWrapper
            getGiftCardBalance={getGiftCardBalance}
            useGiftCard={useGiftCard}
            giftCardModal={giftCardModal}
            giftCardQuery={giftCardQuery}
            closeGiftCardModal={closeGiftCardModal}
            giftCardError={giftCardError}
            giftCardErrorTime={giftCardErrorTime}
          />
        )}
        {!cardPaymentAmount && (
          <Button onClick={handleCompletePayment}>{content.giftCard.completePaymentButton}</Button>
        )}
        <div className={classes.viewBill}>
          <TextButton labelColor={theme.palette.text.primary} onClick={handleViewBill}>
            {content.viewBill}
          </TextButton>
        </div>
      </div>
      {loadingSpinner && <NewLoadingSpinner />}
    </Layout>
  );
};

export default SelectPaymentWrapper;
